import React, { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Label,
  Popup,
  Segment,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from 'semantic-ui-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getWithAuth, postWithAuth } from 'src/api';
import config from 'src/config';
import {
  AccessEntry,
  GeneratedLinks,
  TokenEntry,
  formatDate,
  tokenStatus,
} from './downloadTokens.types';
import { CopyableUrl } from './DownloadTokenForm';
import './DownloadTokenTable.module.css';

function accessSummary(accesses: AccessEntry[], fileType: string) {
  const count = accesses.filter((a) => a.file_type === fileType).length;
  return count === 0 ? '—' : String(count);
}

function ReactivatePopup({
  tokenId,
  onDone,
  onReactivated,
}: {
  tokenId: string;
  onDone: () => void;
  onReactivated: (links: GeneratedLinks) => void;
}) {
  const [hours, setHours] = useState(12);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const data = await postWithAuth(
        `admin/download_token/${tokenId}`,
        { action: 'reactivate', duration_hours: hours },
        'PATCH'
      );
      qc.invalidateQueries({ queryKey: ['admin', 'downloadTokens'] });
      onReactivated(data as GeneratedLinks);
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="download-token-table-reactivate-popup">
      <div className="download-token-table-reactivate-label">
        Extend by (hours)
      </div>
      <Input
        type="number"
        min={1}
        max={168}
        value={hours}
        size="mini"
        className="download-token-table-reactivate-input"
        onChange={(_, { value }) =>
          setHours(Math.min(168, Math.max(1, Number(value))))
        }
      />
      <Button
        size="mini"
        positive
        loading={busy}
        disabled={busy}
        onClick={handleConfirm}
      >
        Confirm
      </Button>
    </div>
  );
}

function TokenActions({
  token,
  onReactivated,
}: {
  token: TokenEntry;
  onReactivated: (links: GeneratedLinks) => void;
}) {
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const qc = useQueryClient();

  const deactivate = useMutation({
    mutationFn: () =>
      postWithAuth(
        `admin/download_token/${token.id}`,
        { action: 'deactivate' },
        'PATCH'
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'downloadTokens'] });
    },
  });

  const status = tokenStatus(token);

  if (status === 'expired') return null;

  if (status === 'active') {
    return (
      <Button
        size="mini"
        basic
        color="red"
        loading={deactivate.isPending}
        disabled={deactivate.isPending}
        onClick={() => deactivate.mutate()}
      >
        Deactivate
      </Button>
    );
  }

  return (
    <Popup
      open={reactivateOpen}
      onClose={() => setReactivateOpen(false)}
      trigger={
        <Button
          size="mini"
          basic
          color="green"
          onClick={() => setReactivateOpen(true)}
        >
          Reactivate
        </Button>
      }
      content={
        <ReactivatePopup
          tokenId={token.id}
          onDone={() => setReactivateOpen(false)}
          onReactivated={onReactivated}
        />
      }
      on="click"
      position="left center"
    />
  );
}

const statusConfig = {
  active: { color: 'green', text: 'Active' },
  deactivated: { color: 'orange', text: 'Deactivated' },
  expired: { color: 'grey', text: 'Expired' },
} as const;

function StatusLabel({ status }: { status: ReturnType<typeof tokenStatus> }) {
  const config = statusConfig[status];
  return (
    <Label color={config.color} size="small">
      {config.text}
    </Label>
  );
}

function DeleteStaleButton({
  staleCount,
  onDelete,
  isLoading,
}: {
  staleCount: number;
  onDelete: () => void;
  isLoading: boolean;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (staleCount === 0) setConfirmDelete(false);
  }, [staleCount]);

  return confirmDelete ? (
    <span>
      <span className="download-token-table-confirm-text">
        Delete {staleCount} token{staleCount !== 1 ? 's' : ''}?
      </span>
      <Button
        size="mini"
        negative
        loading={isLoading}
        disabled={isLoading}
        onClick={onDelete}
      >
        Confirm
      </Button>
      <Button
        size="mini"
        basic
        disabled={isLoading}
        onClick={() => setConfirmDelete(false)}
      >
        Cancel
      </Button>
    </span>
  ) : (
    <Button
      size="mini"
      basic
      color="red"
      disabled={staleCount === 0}
      onClick={() => setConfirmDelete(true)}
    >
      Delete expired &amp; deactivated ({staleCount})
    </Button>
  );
}

type Props = {
  onReactivated: (links: GeneratedLinks) => void;
};

export function DownloadTokenTable({ onReactivated }: Props) {
  const qc = useQueryClient();

  const { data: tokens = [], isFetching } = useQuery<TokenEntry[]>({
    queryKey: ['admin', 'downloadTokens'],
    queryFn: () => getWithAuth('admin/download_tokens'),
  });

  const staleCount = tokens.filter((t) => {
    const s = tokenStatus(t);
    return s === 'expired' || s === 'deactivated';
  }).length;

  const deleteStale = useMutation({
    mutationFn: () => postWithAuth('admin/download_tokens/stale', {}, 'DELETE'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'downloadTokens'] });
    },
  });

  return (
    <Segment>
      <div className="download-token-table-header">
        <h4 className="download-token-table-heading">Recent Tokens</h4>
        <DeleteStaleButton
          staleCount={staleCount}
          onDelete={() => deleteStale.mutate()}
          isLoading={deleteStale.isLoading}
        />
      </div>
      <Table compact celled>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Label</TableHeaderCell>
            <TableHeaderCell>Created by</TableHeaderCell>
            <TableHeaderCell>Expires</TableHeaderCell>
            <TableHeaderCell>Max / file</TableHeaderCell>
            <TableHeaderCell>OVA downloads</TableHeaderCell>
            <TableHeaderCell>Update downloads</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Links</TableHeaderCell>
            <TableHeaderCell />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isFetching && tokens.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} textAlign="center">
                Loading…
              </TableCell>
            </TableRow>
          )}
          {tokens.map((t) => {
            const status = tokenStatus(t);
            return (
              <TableRow
                key={t.id}
                negative={status === 'expired'}
                warning={status === 'deactivated'}
                positive={status === 'active' && t.accesses.length > 0}
              >
                <TableCell>{t.label}</TableCell>
                <TableCell>{t.created_by}</TableCell>
                <TableCell>{formatDate(t.expires_at)}</TableCell>
                <TableCell>{t.max_uses_per_file}</TableCell>
                <TableCell>{accessSummary(t.accesses, 'ova')}</TableCell>
                <TableCell>{accessSummary(t.accesses, 'lpupdate')}</TableCell>
                <TableCell>
                  <StatusLabel status={status} />
                </TableCell>
                <TableCell>
                  {status === 'active' && (
                    <>
                      <CopyableUrl
                        label="OVA"
                        url={`${config.apiURL}/download/ova?token=${t.id}`}
                        showUrl={false}
                      />
                      <CopyableUrl
                        label="Update"
                        url={`${config.apiURL}/download/lpupdate?token=${t.id}`}
                        showUrl={false}
                      />
                    </>
                  )}
                </TableCell>
                <TableCell>
                  <TokenActions token={t} onReactivated={onReactivated} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Segment>
  );
}
